import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Action } from 'redux';

import lang from '../../lib/language';
import { TenNSOROrderStatus } from '../../lib/models/gateway/types';
import { RootState } from '../../lib/store';
import { clearAccountTradeHistory, getAccountTradeHistory } from '../../lib/store/reporting';
import {
  ORDERS_REQUEST_PAGE_NUMBER,
  PENDING_ORDERS_REQUEST_FROM_DATE,
  TRADE_HISTORY_ORDERS_TENNSTATUSES,
} from '../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData } from '../../lib/store/reporting/types';
import { getCurrentAccount } from '../../lib/store/selectors';
import { getDateTime, tradeHistoryCalendarInitialMaxDate } from '../../lib/util/DateTimeHelpers';

import ArrowBack from '../../assets/img/blue_icon-arrow-left.svg';
import iconClear from '../../assets/img/icon-clear.svg';
import { ClickableImage, Grid } from '../../components';
import { useIsMobile } from '../../hooks';
import DateRangePickerWrapper from '../../shared_components/DesktopCalendar/DateRangePickerWrapper';
import MobileFromToDatePicker from '../../shared_components/MobileCalendar/MobileFromToDatePicker';

import StatusSelector from './StatusSelector/StatusSelector';
import TradeHistoryTable from './TradeHistoryTable/TradeHistoryTable';
import SearchSymbols from './SearchSymbols';
import { statusOptionsLabels } from './TradeHistoryOptions';
import { useStyles } from './TradeHistoryStyles';

import styles from './TradeHistory.module.scss';

const statusFilterTitle = lang.commonOrderStatusFilterTitle();
const statusFiltersWithStatusTitle = [statusFilterTitle, ...statusOptionsLabels];
const OrderStatusToTenNSORMapping = {
  [lang.commonOrderStatusFilterFilled()]: TenNSOROrderStatus.Filled,
  [lang.commonOrderStatusFilterPartiallyFilled()]: TenNSOROrderStatus.PartialFill,
  [lang.commonOrderStatusFilterPartiallyRejected()]: TenNSOROrderStatus.PartialReject,
  [lang.commonOrderStatusCanceled()]: TenNSOROrderStatus.Cancelled,
  [lang.commonOrderStatusRejected()]: TenNSOROrderStatus.Rejected,
  [lang.commonOrderStatusFilterExpired()]: TenNSOROrderStatus.Expired,
};

type Props = {} & ReduxProps;

const TradeHistory = (props: Props) => {
  const {
    accountReferenceId,
    accountTradeHistory,
    getTradeHistory,
    clearAccountTradeHistoryData,
  } = props;
  const classes = useStyles();
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  let locationState = location.state as { symbol: string } | null;
  if (!locationState || !locationState.symbol) {
    locationState = null;
  }

  const [statusOption, setStatusOption] = useState<string>(statusFilterTitle);
  const [searchValue, setSearchValue] = useState<string>(locationState ? locationState.symbol : '');
  const [pageNumber, setPageNumber] = useState<number>(ORDERS_REQUEST_PAGE_NUMBER);
  const [rangeDate, setRangeDate] = useState({ startDate: '', endDate: '' });

  const handleChangeByStatus = (event) => {
    const selectedValue = isMobile ? event : event.target.value;
    setStatusOption(selectedValue);
  };

  const resetTradeHistory = (symbol: string) => {
    clearAccountTradeHistoryData();
    const { startDate, endDate } = rangeDate;

    getTradeHistory({
      fromDate: startDate.length > 0 ? startDate : PENDING_ORDERS_REQUEST_FROM_DATE,
      toDate: endDate.length > 0 ? endDate : getDateTime(),
      tenNStatuses_StringList: TRADE_HISTORY_ORDERS_TENNSTATUSES,
      pageNumber: ORDERS_REQUEST_PAGE_NUMBER,
      symbols_StringList: symbol.length !== 0 ? symbol : undefined,
    });
  };

  const handleSymbolReset = () => {
    setSearchValue('');
    resetTradeHistory('');
  };

  const handleReset = () => {
    setStatusOption(statusFilterTitle);
    resetTradeHistory(searchValue);
  };

  const handleBtnClose = () => (
    <>
      {
        (statusOption !== statusFilterTitle)
          ? <ClickableImage src={iconClear} onClick={handleReset} className={classes.btnClose} />
          : <></>
      }
    </>
  );

  const handleMenuItem = (item: string) => {
    if (item === lang.commonOrderStatusFilterTitle()) {
      return <div className={classes.initialText}>{item}</div>;
    }
    return <div className={classes.selectedText}>{item}</div>;
  };

  const handleSearchSymbol = (symbol: string) => {
    setSearchValue(symbol);
    setPageNumber(ORDERS_REQUEST_PAGE_NUMBER);
    clearAccountTradeHistoryData();

    const { startDate, endDate } = rangeDate;
    const tenNStatus = OrderStatusToTenNSORMapping[statusOption];

    if (symbol.length === 0) {
      getTradeHistory({
        fromDate: startDate.length > 0 ? startDate : PENDING_ORDERS_REQUEST_FROM_DATE,
        toDate: endDate.length > 0 ? endDate : getDateTime(),
        pageNumber: ORDERS_REQUEST_PAGE_NUMBER,
        tenNStatuses_StringList: statusOption !== lang.commonOrderStatusFilterTitle()
          ? tenNStatus
          : TRADE_HISTORY_ORDERS_TENNSTATUSES,
      });
    }
  };

  const handleRowsPerPage = () => {
    setPageNumber(num => num + ORDERS_REQUEST_PAGE_NUMBER);
  };

  useEffect(() => {
    if (rangeDate.startDate !== '' && rangeDate.endDate !== '') {
      clearAccountTradeHistoryData();
      setPageNumber(ORDERS_REQUEST_PAGE_NUMBER);

      const { startDate, endDate } = rangeDate;
      const tenNStatus = OrderStatusToTenNSORMapping[statusOption];

      getTradeHistory({
        fromDate: startDate,
        toDate: endDate,
        pageNumber: ORDERS_REQUEST_PAGE_NUMBER,
        tenNStatuses_StringList: statusOption !== lang.commonOrderStatusFilterTitle()
          ? tenNStatus
          : TRADE_HISTORY_ORDERS_TENNSTATUSES,
        symbols_StringList: searchValue.length > 0 ? searchValue : undefined,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDate.startDate, rangeDate.endDate]);

  useEffect(() => {
    if (statusOption !== lang.commonOrderStatusFilterTitle()) {
      const { startDate, endDate } = rangeDate;

      const tenNStatuses_StringList = OrderStatusToTenNSORMapping[statusOption];

      clearAccountTradeHistoryData();
      setPageNumber(ORDERS_REQUEST_PAGE_NUMBER);
      getTradeHistory({
        fromDate: startDate.length > 0 ? startDate : PENDING_ORDERS_REQUEST_FROM_DATE,
        toDate: endDate.length > 0 ? endDate : getDateTime(),
        pageNumber: ORDERS_REQUEST_PAGE_NUMBER,
        tenNStatuses_StringList,
        symbols_StringList: searchValue.length !== 0 ? searchValue : undefined,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOption]);

  useEffect(() => {
    if (searchValue.length !== 0) {
      const { startDate, endDate } = rangeDate;
      const tenNStatus = OrderStatusToTenNSORMapping[statusOption];

      getTradeHistory({
        fromDate: startDate.length > 0 ? startDate : PENDING_ORDERS_REQUEST_FROM_DATE,
        toDate: endDate.length > 0 ? endDate : getDateTime(),
        pageNumber: ORDERS_REQUEST_PAGE_NUMBER,
        tenNStatuses_StringList: statusOption !== lang.commonOrderStatusFilterTitle()
          ? tenNStatus
          : TRADE_HISTORY_ORDERS_TENNSTATUSES,
        symbols_StringList: searchValue,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (pageNumber > ORDERS_REQUEST_PAGE_NUMBER) {
      const { startDate, endDate } = rangeDate;
      const tenNStatus = OrderStatusToTenNSORMapping[statusOption];

      getTradeHistory({
        fromDate: startDate.length > 0 ? startDate : PENDING_ORDERS_REQUEST_FROM_DATE,
        toDate: endDate.length > 0 ? endDate : getDateTime(),
        pageNumber,
        tenNStatuses_StringList: statusOption !== lang.commonOrderStatusFilterTitle()
          ? tenNStatus
          : TRADE_HISTORY_ORDERS_TENNSTATUSES,
        symbols_StringList: searchValue.length > 0 ? searchValue : undefined,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  useEffect(() => {
    clearAccountTradeHistoryData();
    if (!locationState && accountReferenceId) {
      getTradeHistory({
        pageNumber: ORDERS_REQUEST_PAGE_NUMBER,
        tenNStatuses_StringList: TRADE_HISTORY_ORDERS_TENNSTATUSES,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAccountTradeHistoryData, getTradeHistory, accountReferenceId]);

  const handleDateChange = (value) => {
    const { startDate, endDate } = value;

    if (startDate === new Date().toISOString().split('T')[0] && endDate === new Date().toISOString().split('T')[0]) {
      setRangeDate({
        ...rangeDate,
        startDate: getDateTime(),
        endDate: getDateTime(),
      });
    } else {
      setRangeDate({ ...rangeDate, startDate, endDate });
    }
  };

  return (
    <Grid>
      <div className={styles.wrapper}>
        { isMobile && (
        <div className={styles.arrowBackCapital}>
          <ClickableImage
            src={ArrowBack}
            onClick={() => navigate(-1)}
          />
        </div>
        )}
        <h6 className={styles.title}>{lang.commonTitleHistory()}</h6>
        <div className={styles.wrapperTable}>
          <div className={classes.wrapperHeaderItems}>
            <div className={styles.dateAndStatusWrapper}>
              <div className={styles.dateAndStatus}>
                { isMobile ? (
                  <MobileFromToDatePicker
                    label={lang.commonDate()}
                    initialState={{
                      startDate: new Date(getDateTime(true, [ 'minus' ], [ [ 3, 'months' ] ])),
                      endDate: tradeHistoryCalendarInitialMaxDate(),
                    }}
                    maxDateCalendar={tradeHistoryCalendarInitialMaxDate()}
                    onDateSelect={handleDateChange}
                  />
                ) : (
                  <DateRangePickerWrapper
                    initialState={{
                      startDate: new Date(getDateTime(true, [ 'minus' ], [ [ 3, 'months' ] ])),
                      endDate: tradeHistoryCalendarInitialMaxDate(),
                    }}
                    maxDateCalendar={tradeHistoryCalendarInitialMaxDate()}
                    onDateSelect={handleDateChange}
                  />
                )}
                <div className={classes.wrapperStatusByItems} style={{ marginTop: isMobile ? '0' : '5px' }}>
                  <StatusSelector
                    currentStatus={statusOption}
                    handleChangeByStatus={handleChangeByStatus}
                    handleBtnClose={handleBtnClose}
                    statusList={statusFiltersWithStatusTitle}
                    handleMenuItem={handleMenuItem}
                    handleReset={handleReset}
                  />
                </div>
                { isMobile && (
                <div className={classes.wrapperSearchFieldMobile}>
                  <SearchSymbols
                    handleSearchSymbol={handleSearchSymbol}
                    searchValue={searchValue}
                    handleReset={handleSymbolReset}
                  />
                </div>
                )}
              </div>
            </div>
            { !isMobile && (
            <div className={classes.wrapperSearchField}>
              <SearchSymbols
                handleSearchSymbol={handleSearchSymbol}
                searchValue={searchValue}
              />
            </div>
            )}
          </div>
          <div className={styles.tableWrapper}>
            <TradeHistoryTable
              accountTradeHistory={accountTradeHistory}
              handleRowsPerPage={handleRowsPerPage}
            />
          </div>
        </div>
      </div>
    </Grid>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountTradeHistory: state.reporting.tradeHistoryOrders,
  accountReferenceId: getCurrentAccount(state),
});
const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getTradeHistory: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  clearAccountTradeHistoryData: () => dispatch(clearAccountTradeHistory()),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TradeHistory);
